import { dataServices, Promise, Realty, services } from '@ambita/ambita-components-core';
import addressesTransformer from './transformers/cadastreReport/addresses';
import affairsTransformer from './transformers/cadastreReport/affairs';
import buildingTransformer from './transformers/cadastreReport/building';
import cadastreConveyancesTransformer from './transformers/cadastreReport/cadastreConveyances';
import cadastreOwnershipsTransformer from './transformers/cadastreReport/cadastreOwnerships';
import complaintsTransformer from './transformers/cadastreReport/complaints';
import contactsTransformer from './transformers/cadastreReport/contacts';
import culturalHeritagesTransformer from './transformers/cadastreReport/culturalHeritages';
import developersTransformer from './transformers/cadastreReport/developers';
import districtsTransformer from './transformers/cadastreReport/districts';
import floorsTransformer from './transformers/cadastreReport/floors';
import owningCadastresTransformer from './transformers/cadastreReport/owningCadastres';
import personTransformer from './transformers/cadastreReport/person';
import plotsTransformer from './transformers/cadastreReport/plots';
import sectioningsTransformer from './transformers/cadastreReport/sectionings';
import singleHeritagesTransformer from './transformers/cadastreReport/singleHeritages';
import soilContaminationsTransformer from './transformers/cadastreReport/soilContaminations';
import separatelyOccupiedUnitsTransformer from './transformers/cadastreReport/separatelyOccupiedUnits';
import unregisteredOwnershipsTransformer from './transformers/cadastreReport/unregisteredOwnerships';
import { ApiUnregisteredOwnership } from '../typings/Owner';
import { SectionWithAddress, TransformedBuilding, TransformedUnit } from '../typings/CadastreReport';

const { realty, xhr } = dataServices;
const { filters, log, util } = services;

const defaultRequestParameters = {
  pagesize: 1000
};

const getResourceByLinkAndTransform = (transformer: any, requestParameters?: any): (
  url: string,
  transformerData?: any
) => Promise<any> => {
  return (url: string, transformerData?: any): Promise<any> => {
    const params = {
      ...defaultRequestParameters,
      ...requestParameters
    };
    url = util.addUrlParams(url, params);

    return xhr.getJSON(url, { cache: true })
      .then(result => transformer(result, transformerData));
  };
};

const defaultTransformer = (response: Realty.ApiResponse): any[] => {
  if (response.items && response.items.length > 0) {
    return response.items;
  }
};

const getSectioningsByLink = getResourceByLinkAndTransform(sectioningsTransformer);
const getSingleHeritagesByLink = getResourceByLinkAndTransform(singleHeritagesTransformer);
const getSoilContaminationsByLink = getResourceByLinkAndTransform(soilContaminationsTransformer);
const getComplaintsByLink = getResourceByLinkAndTransform(complaintsTransformer);
const getAffairsByLink = getResourceByLinkAndTransform(affairsTransformer);
const getDistrictsByLink = getResourceByLinkAndTransform(districtsTransformer);
const getFloorsByLink = getResourceByLinkAndTransform(floorsTransformer);
const getDevelopersByLink = getResourceByLinkAndTransform(developersTransformer);
const getContactsByLink = getResourceByLinkAndTransform(contactsTransformer);
const getCulturalHeritagesByLink = getResourceByLinkAndTransform(culturalHeritagesTransformer);
const getAddressesByLink = getResourceByLinkAndTransform(addressesTransformer);
const getCadastreOwnershipsByLink = getResourceByLinkAndTransform(cadastreOwnershipsTransformer);
const getSeparatelyOccupiedUnitsByLink = getResourceByLinkAndTransform(separatelyOccupiedUnitsTransformer);
const getPersonByLink = getResourceByLinkAndTransform(personTransformer);
const getUnregisteredOwnershipsByLink = getResourceByLinkAndTransform(unregisteredOwnershipsTransformer);
const getOwningCadastresByLink = getResourceByLinkAndTransform(owningCadastresTransformer);
const getPlotsByLink = getResourceByLinkAndTransform(plotsTransformer);
const getCadastreConveyancesByLink = getResourceByLinkAndTransform(cadastreConveyancesTransformer, {
  page: 1,
  pagesize: 1
});
const getOwnershipsOwnedByLink = getResourceByLinkAndTransform(defaultTransformer);
const getBuildingsByLink = getResourceByLinkAndTransform(defaultTransformer);
const getBuildingChangesByLink = getResourceByLinkAndTransform(defaultTransformer);

const addCadastreBaseProperties = (cadastreData: Realty.ApiCadastre): any => {
  const lookupData = util.pick(cadastreData, [
    'agriculturalCadastre',
    'areaSource',
    'establishedDate',
    'hasActiveLeasedLand',
    'ident',
    'isRegisteredLand',
    'isTechnicalRegisteredLand',
    'isHistoricalTechnicalRegisteredLand',
    'isSectioned',
    'landTaxation',
    'mainCadastre',
    'municipality',
    'numberOfPlots',
    'propertyType',
    'specifiedArea',
    'unitName',
    'zeroConcession',
    'zeroConcessionMessage'
  ]
  );

  lookupData.cadastre = filters.cadastre(cadastreData.ident);

  return lookupData;
};

const deleteLinksAndMerge = (objectToMerge: any, resultToMerge: any): any => {
  delete objectToMerge.links;

  return util.merge(objectToMerge, resultToMerge);
};

const getOwningPersonPromise = (owner: ApiUnregisteredOwnership): Promise<any> => {
  return getPersonByLink(owner.links.owningPerson.href)
    .then(result => deleteLinksAndMerge(owner, result));
};

const getOwningCadastrePromise = (owner: any): Promise<any> => {
  return getOwningCadastresByLink(owner.links.owningCadastre.href)
    .then(result => deleteLinksAndMerge(owner, result));
};

const getOwnershipsOwningPromise = (cadastreData: Realty.ApiCadastre): Promise<any> => {
  return getCadastreOwnershipsByLink(cadastreData.links.ownershipsOwningThisCadastre.href)
    .then(ownerships => ({ owners: ownerships }))
    .catch(error => {
      log.error(error);
    });
};

const getUnregisteredOwnershipsOwningPromise = (cadastreData: Realty.ApiCadastre): Promise<any> => {
  return getUnregisteredOwnershipsByLink(cadastreData.links.unregisteredOwnershipsOwningThisCadastre.href)
    .then(ownerships => {
      if (ownerships && ownerships.unregisteredOwnerships.length > 0) {
        const personPromises: Promise<any>[] = [];
        const cadastrePromises: Promise<any>[] = [];
        const returnObject: any = {};

        ownerships.unregisteredOwnerships.forEach(owner => {
          if (owner.links.owningPerson) {
            personPromises.push(getOwningPersonPromise(owner));
          } else if (owner.links.owningCadastre) {
            cadastrePromises.push(getOwningCadastrePromise(owner));
          }
        });

        const getPersonPromises = Promise.all(personPromises)
          .then(result => {
            if (result.length > 0) {
              returnObject.unregisteredOwnerships = result;
            }
          });

        const getCadastresPromises = Promise.all(cadastrePromises)
          .then(result => {
            if (result.length > 0) {
              returnObject.unregisteredCadastreOwnerships = result;
            }
          });

        return Promise.all([getPersonPromises, getCadastresPromises])
          .then(() => returnObject);
      }
    })
    .catch(error => {
      log.error(error);
    });
};

const getOwnershipsOwnedPromise = (cadastreData: Realty.ApiCadastre): Promise<any> => {
  return getOwnershipsOwnedByLink(cadastreData.links.ownershipsOwnedByThisCadastre.href)
    .then(ownerships => {
      if (ownerships) {
        return {
          ownershipsOwned: ownerships
        };
      }
    })
    .catch(error => {
      log.error(error);
    });
};

const getBuildingsAndChangesPromise = (cadastreData: Realty.ApiCadastre): Promise<any> => {
  return getBuildingsByLink(cadastreData.links.buildings.href)
    .then(buildings => {
      if (buildings) {
        const buildingChangesPromises: Promise<any>[] = [];

        buildings.forEach(building => {
          buildingChangesPromises.push(getBuildingChangesByLink(building.links.buildingchanges.href));
        });

        return Promise.all(buildingChangesPromises)
          .then(buildingChanges => {
            const allBuildingsAndChanges = [...buildings, ...[].concat(...buildingChanges)].filter(b => b);
            const buildingsWithoutApprovedDate = allBuildingsAndChanges.filter(b => !b.approvedDate);
            const buildingsSortedByApprovedDate = allBuildingsAndChanges
              .filter(b => b.approvedDate)
              .sort((a, b): number => {
                return new Date(b.approvedDate.timestamp).valueOf() - new Date(a.approvedDate.timestamp).valueOf();
              });

            return {
              buildings: [...buildingsSortedByApprovedDate, ...buildingsWithoutApprovedDate]
            };
          });
      }
    })
    .catch(error => {
      log.error(error);
    });
};

const assembleDistrictsAndAddresses = (districtsCollection: any): any => {
  const distinctDistrictsAndAddresses = [];

  for (const district of districtsCollection) {
    const districtWithoutAddress = util.clone(district);
    delete districtWithoutAddress.addresses;

    let containsDistrict = false;

    containsDistrict = distinctDistrictsAndAddresses.some((distinctDistrict) => {
      const distinctDistrictWithoutAddress = util.clone(distinctDistrict);
      delete distinctDistrictWithoutAddress.addresses;

      return util.shallowEqual(districtWithoutAddress, distinctDistrictWithoutAddress);
    });

    // If district doesn't exist, push it into final result array
    if (!containsDistrict) {
      distinctDistrictsAndAddresses.push(district);
    } else { // If it does exist, find the district and add the address to it
      for (const distinctDistrict of distinctDistrictsAndAddresses) {
        const distinctDistrictWithoutAddress = util.clone(distinctDistrict);
        delete distinctDistrictWithoutAddress.addresses;

        if (util.shallowEqual(districtWithoutAddress, distinctDistrictWithoutAddress)) {
          distinctDistrict.addresses.push(...district.addresses);
          break;
        }
      }
    }
  }

  return distinctDistrictsAndAddresses;
};

const getDistrictsPromise = (cadastreData: Realty.ApiCadastre): Promise<any> => {
  return getAddressesByLink(cadastreData.links.addresses.href)
    .then(addressCollection => {
      if (addressCollection && addressCollection.addresses && addressCollection.addresses.length > 0) {
        const districtPromises: Promise<any>[] = [];

        addressCollection.addresses.forEach((address: Realty.Address) => {
          const link = address.links.districts.href;

          delete address.links;
          districtPromises.push(getDistrictsByLink(link, { address }));
        });

        return Promise.all(districtPromises).then(districtsCollection => {
          if (districtsCollection) {
            const assembledDistrictsAndAddresses = assembleDistrictsAndAddresses(districtsCollection);

            return {
              districts: assembledDistrictsAndAddresses
            };
          }
        });
      }
    });
};

const buildingPromise = (unit: TransformedUnit): Promise<any> => {
  return xhr.getJSON(unit.links.building.href, { cache: true })
    .then(building => {
      delete unit.links;

      const transformedBuilding = buildingTransformer(building, { unit });

      if (transformedBuilding) {
        return transformedBuilding;
      }
    })
    .catch(error => {
      log.error(error);
    });
};

const assembleBuildingsAndUnits = (buildings: any): any => {
  const distinctBuildingsAndUnits = [];

  for (const building of buildings) {
    const containsBuilding = distinctBuildingsAndUnits.some(distinctBuilding => {
      return distinctBuilding.buildingNumber === building.buildingNumber;
    });

    if (!containsBuilding) {
      distinctBuildingsAndUnits.push(building);
    } else {
      for (const distinctBuilding of distinctBuildingsAndUnits) {
        if (distinctBuilding.buildingNumber === building.buildingNumber) {
          distinctBuilding.units.push(...building.units);
          break;
        }
      }
    }
  }

  return distinctBuildingsAndUnits;
};

const getUnitAddressPromise = (unit: TransformedUnit): Promise<any> => {
  return new Promise(resolve => {
    if (unit.links.address) {
      return getAddressesByLink(unit.links.address.href)
        .then(address => {
          if (address) {
            address = address.addresses[0];
            delete address.links;
            unit.address = address;
          }

          resolve(unit);
        });
    } else {
      resolve(unit);
    }
  });
};

const getBuildingsAndUnitsPromise = (cadastreData: Realty.ApiCadastre): Promise<any> => {
  return getSeparatelyOccupiedUnitsByLink(cadastreData.links.units.href)
    .then(units => {
      if (!units || !units.separatelyOccupiedUnits || units.separatelyOccupiedUnits.length < 1) {
        log.info('No units found');

        return Promise.resolve([]);
      }

      units = units.separatelyOccupiedUnits;
      const unitAddressPromises: Promise<any>[] = [];

      units.forEach((unit: TransformedUnit) => {
        unitAddressPromises.push(getUnitAddressPromise(unit));
      });

      return Promise.all(unitAddressPromises);
    })
    .then((units: TransformedUnit[]) => {
      return new Promise(resolve => {
        const buildingPromises: Promise<any>[] = [];

        units.forEach((unit: TransformedUnit) => {
          buildingPromises.push(buildingPromise(unit));
        });

        Promise.all(buildingPromises)
          .then(result => {
            const purgedResult = result.filter(element => !!element);
            const assembledBuildingsAndUnits = assembleBuildingsAndUnits(purgedResult);

            resolve(assembledBuildingsAndUnits);
          });
      });
    })
    .catch(error => {
      log.error(error);
    });
};

const addFloorToBuildingPromise = (building: TransformedBuilding): Promise<any> => {
  return getFloorsByLink(building.links.floors.href)
    .then(floors => {
      if (floors) {
        building.floors = floors.floors;
      }
    });
};

const addFloorsToBuildingsPromise = (buildings: TransformedBuilding[]): Promise<any> => {
  return new Promise(resolve => {
    const floorPromises: Promise<any>[] = [];

    buildings.forEach((unit: TransformedBuilding) => {
      floorPromises.push(addFloorToBuildingPromise(unit));
    });

    Promise.all(floorPromises)
      .then(() => resolve(buildings));
  });
};

const addDeveloperToBuildingPromise = (building: TransformedBuilding): Promise<any> => {
  return getDevelopersByLink(building.links.developers.href)
    .then(developers => {
      if (developers) {
        building.contacts ? building.contacts.push(...developers) : building.contacts = developers.developers;
      }
    });
};

const addContactToBuildingPromise = (building: TransformedBuilding): Promise<any> => {
  return getContactsByLink(building.links.contacts.href)
    .then(contacts => {
      if (contacts) {
        building.contacts ? building.contacts.push(...contacts) : building.contacts = contacts.contacts;
      }
    });
};

const addContactsToBuildingsPromise = (buildings: TransformedBuilding[]): Promise<any> => {
  return new Promise(resolve => {
    const contactPromises: Promise<any>[] = [];

    buildings.forEach((building: TransformedBuilding) => {
      contactPromises.push(addDeveloperToBuildingPromise(building));
      contactPromises.push(addContactToBuildingPromise(building));
    });

    Promise.all(contactPromises)
      .then(() => resolve(buildings));
  });
};

const addCulturalHeritageToBuildingPromise = (building: TransformedBuilding): Promise<any> => {
  return getCulturalHeritagesByLink(building.links.culturalheritages.href)
    .then(culturalHeritages => {
      if (culturalHeritages) {
        building.culturalHeritages = culturalHeritages.culturalHeritages;
      }
    });
};

const addCulturalHeritagesToBuildingsPromise = (buildings: TransformedBuilding[]): Promise<any> => {
  return new Promise(resolve => {
    const culturalHeritagePromises: Promise<any>[] = [];

    buildings.forEach((building: TransformedBuilding) => {
      culturalHeritagePromises.push(addCulturalHeritageToBuildingPromise(building));
    });

    Promise.all(culturalHeritagePromises)
      .then(() => resolve(buildings));
  });
};

const addSingleHeritageToBuildingPromise = (building: TransformedBuilding): Promise<any> => {
  return getSingleHeritagesByLink(building.links.singleheritages.href)
    .then(singleHeritages => {
      if (singleHeritages) {
        building.singleHeritages = singleHeritages.singleHeritages;
      }
    });
};

const addSingleHeritagesToBuildingsPromise = (buildings: TransformedBuilding[]): Promise<any> => {
  return new Promise(resolve => {
    const singleHeritagePromises: Promise<any>[] = [];

    buildings.forEach((building: TransformedBuilding) => {
      singleHeritagePromises.push(addSingleHeritageToBuildingPromise(building));
    });

    Promise.all(singleHeritagePromises)
      .then(() => resolve(buildings));
  });
};

const addHighlightOfFloorUnitBelongsTo = (lookupData: any): any => {
  // Check if this is a section and flag floor
  if (lookupData.buildings && lookupData.propertyType && lookupData.propertyType.code === '5') {
    lookupData.buildings.forEach(building => {
      if (building.units) {
        building.units.forEach(unit => {
          if (unit.floorNumber && building.floors) {
            building.floors.forEach(floor => {
              if (floor.floorNumber === unit.floorNumber) {
                floor.unitFloor = true;
              }
            });
          }
        });
      }
    });
  }

  return lookupData;
};

const getBuildingDetailsPromise = (buildingsPromise: Promise<any>): Promise<any> => {
  return buildingsPromise
    .then((buildings: TransformedBuilding[]) => {
      if (!buildings) {
        log.info('No buildings found');

        return Promise.resolve([]);
      }

      return addFloorsToBuildingsPromise(buildings);
    })
    .then(addContactsToBuildingsPromise)
    .then(addSingleHeritagesToBuildingsPromise)
    .then((buildings: TransformedBuilding[]) => {
      return addCulturalHeritagesToBuildingsPromise(buildings)
        .then((assembledBuildings: TransformedBuilding[]) => {
          for (const building of assembledBuildings) {
            delete building.links;
          }

          return {
            buildings: assembledBuildings
          };
        });
    })
    .catch(error => {
      log.error(error);
    });
};

const getSectionWithAddress = (sectionData: Realty.ApiCadastre): Promise<SectionWithAddress> => {
  const dataPromises = [
    realty.getResourceByLink(sectionData.links.cadastre.href),
    realty.getResourceByLink(sectionData.links.addresses.href)
  ];

  return Promise.all(dataPromises)
    .then(([cadastreResponse, addressesResponse]) => {
      const section = cadastreResponse.item;
      section.sectionAddress = addressesResponse.items[0];

      return section;
    });
};

const getSectionsPromise = (cadastreData: Realty.ApiCadastre): Promise<any> => {
  const cadastreKey = cadastreData.key.slice(0, cadastreData.key.lastIndexOf('-'));
  const cadastreQuery = {
    query: cadastreKey,
    pagesize: 1000
  };

  return realty.queryCadastres(cadastreQuery)
    .then(queryResult => {
      if (!Array.isArray(queryResult.items)) {
        return Promise.resolve([]);
      }

      const sectionsPromises = queryResult.items
        .filter(cadastre => cadastre.ident.sectionNumber > 0)
        .map(getSectionWithAddress);

      return Promise.all(sectionsPromises);
    })
    .then((sections: SectionWithAddress[]) => ({ sections }));
};

const getPlotsAreaNotesPromise = (cadastreData: Realty.ApiCadastre): Promise<any> => {
  return getPlotsByLink(cadastreData.links.plots.href)
    .then(plots => ({ plots }));
};

export const generateCadastreLookupFromCadastreData = (cadastreData: Realty.ApiCadastre): Promise<any> => {
  const lookupData = addCadastreBaseProperties(cadastreData);
  const lookupPromises: Promise<any>[] = [];

  if (cadastreData.isSectioned) {
    lookupPromises.push(getSectionsPromise(cadastreData));
  } else {
    lookupPromises.push(getOwnershipsOwningPromise(cadastreData));
  }

  lookupPromises.push(getUnregisteredOwnershipsOwningPromise(cadastreData));

  lookupPromises.push(getPlotsAreaNotesPromise(cadastreData));
  lookupPromises.push(getSectioningsByLink(cadastreData.links.sectionings.href));

  if (cadastreData.hasSingleHeritage) {
    lookupPromises.push(getSingleHeritagesByLink(cadastreData.links.singleHeritages.href));
  }

  if (cadastreData.hasSoilContamination) {
    lookupPromises.push(getSoilContaminationsByLink(cadastreData.links.soilContaminations.href));
  }

  if (cadastreData.hasNotedComplaint) {
    lookupPromises.push(getComplaintsByLink(cadastreData.links.complaints.href));
  }

  lookupPromises.push(getCadastreConveyancesByLink(cadastreData.links.conveyances.href));
  lookupPromises.push(getDistrictsPromise(cadastreData));
  lookupPromises.push(getAffairsByLink(cadastreData.links.affairs.href));

  // TODO when searching for units / buildings we need to get the unit / building changes as well
  const getBuildingsAndUnits = getBuildingsAndUnitsPromise(cadastreData);
  lookupPromises.push(getBuildingDetailsPromise(getBuildingsAndUnits));
  lookupPromises.push(getAddressesByLink(cadastreData.links.addresses.href));

  return Promise.all(lookupPromises)
    .then(data => util.merge(lookupData, ...data))
    .catch(Promise.reject);
};

const fetchBrokerLetterReportDataFromCadastreData = (cadastreData: Realty.ApiCadastre): Promise<any> => {
  const lookupData = addCadastreBaseProperties(cadastreData);
  const lookupPromises: Promise<any>[] = [];

  if (!cadastreData.isSectioned) {
    lookupPromises.push(getOwnershipsOwningPromise(cadastreData));
  }

  lookupPromises.push(getUnregisteredOwnershipsOwningPromise(cadastreData));
  lookupPromises.push(getOwnershipsOwnedPromise(cadastreData));
  lookupPromises.push(getAddressesByLink(cadastreData.links.addresses.href));
  lookupPromises.push(getSeparatelyOccupiedUnitsByLink(cadastreData.links.units.href));
  lookupPromises.push(getBuildingsAndChangesPromise(cadastreData));

  return Promise.all(lookupPromises)
    .then(data => util.merge(lookupData, ...data))
    .catch(Promise.reject);
};

const isMainCadastre = (cadastreIdent: Realty.CadastreIdent): boolean => {
  return cadastreIdent.leaseholdUnitNumber === 0 && cadastreIdent.sectionNumber === 0;
};

const getMainCadastreOwners = (response: Realty.ApiResponse): Promise<any> => {
  const cadastreData = response.item;
  const cadastreIdent = cadastreData.ident;

  if (!isMainCadastre(cadastreIdent)) {
    const mainCadastre =
      `${cadastreIdent.municipalityNumber}-${cadastreIdent.cadastralUnitNumber}-${cadastreIdent.unitNumber}-0-0`;

    return realty.getCadastres(mainCadastre)
      .then((result: Realty.ApiResponse) => {
        const mainCadastreData: Realty.ApiCadastre = result.item;

        return getOwnershipsOwningPromise(mainCadastreData)
          .then(owners => {
            return {
              ...cadastreData,
              mainCadastre: owners
            };
          })
          .then(newCadastreData => {
            return getUnregisteredOwnershipsOwningPromise(mainCadastreData)
              .then(unregisteredOwnerships => {
                return {
                  ...newCadastreData,
                  mainCadastre: {
                    ...newCadastreData.mainCadastre,
                    ...unregisteredOwnerships
                  }
                };
              })
              .catch(Promise.reject);
          })
          .catch(Promise.reject);
      })
      .catch(error => {
        log.error(error);
        return Promise.reject(new Error('Kunne ikke hente informasjon om matrikkelen'));
      });
  }

  return Promise.resolve(cadastreData);
};

export const generateCadastreLookupFromQuery = (query: any): Promise<any> => {
  const cadastreQuery = query;

  if (cadastreQuery.cadastre) {
    return realty.getCadastres(cadastreQuery.cadastre)
      .then(getMainCadastreOwners)
      .then(generateCadastreLookupFromCadastreData)
      .then(addHighlightOfFloorUnitBelongsTo)
      .catch(error => {
        log.error(error);
        return Promise.reject(new Error('Kunne ikke hente informasjon om matrikkelen'));
      });
  } else {
    return Promise.reject(new Error('Feil i matrikkel'));
  }
};

export const fetchBrokerLetterReportData = (cadastreKey: string): Promise<any> => {
  return realty.getCadastres(cadastreKey)
    .then(getMainCadastreOwners)
    .then(fetchBrokerLetterReportDataFromCadastreData)
    .catch(error => {
      log.error(error);
      return Promise.reject(new Error('Could not gather all information about the broker letter report'));
    });
};
